import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';
import { Moon, Star } from 'lucide-react';
export function Hero() {
  const videoSectionRef = useRef<HTMLElement>(null);
  const scrollToBonuses = () => {
    const bonosSection = document.getElementById('bonos');
    if (bonosSection) {
      bonosSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      const section = videoSectionRef.current;
      if (!section) return;
      const max = window.innerHeight;
      const opacity = Math.max(0, 1 - window.scrollY / max);
      section.style.opacity = opacity.toString();
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="relative w-full h-screen overflow-hidden" data-id="element-142">
      {/* Background Layer: Video Section */}
      <section ref={videoSectionRef} id="hero-video-section" className="absolute inset-0" style={{
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      opacity: 1,
      zIndex: 0
    }} data-id="element-143">
        <iframe src="https://player.vimeo.com/video/1143661603?background=1&autoplay=1&loop=1&muted=1" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block'
      }} data-id="element-144" />
      </section>

      {/* Overlay Layer: Dark Content Section */}
      <section className="absolute inset-0 flex items-center justify-center px-4 py-20" style={{
      zIndex: 2,
      background: 'rgba(12, 15, 23, 0.75)'
    }} data-id="element-145">
        {/* Mystical Gradient Overlays */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-146">
          <motion.div animate={{
          opacity: [0.08, 0.15, 0.08]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#5B86FF] rounded-full blur-[120px]" data-id="element-147" />
          <motion.div animate={{
          opacity: [0.06, 0.12, 0.06]
        }} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#CFA86A] rounded-full blur-[120px]" data-id="element-148" />

          {/* Floating Stars */}
          <motion.div animate={{
          y: [0, -8, 0],
          opacity: [0.3, 0.6, 0.3]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-20 right-20 text-[#CFA86A]" data-id="element-149">
            <Star className="w-6 h-6 fill-[#CFA86A]" data-id="element-150" />
          </motion.div>
          <motion.div animate={{
          y: [0, -6, 0],
          opacity: [0.25, 0.5, 0.25]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2
        }} className="absolute bottom-40 left-20 text-[#5B86FF]" data-id="element-151">
            <Moon className="w-8 h-8" data-id="element-152" />
          </motion.div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-153">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-8 flex justify-center" data-id="element-154">
            <span className="px-4 py-1 rounded-full border border-[#CFA86A]/30 text-[#CFA86A] text-sm tracking-widest uppercase bg-[#0A0D14]/50 backdrop-blur-sm" data-id="element-155">
              Tarot Terapéutico & Autoconocimiento
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-5xl md:text-7xl font-bold text-[#F7F5F2] mb-8 leading-tight font-serif drop-shadow-2xl" data-id="element-156">
            Decisiones Que{' '}
            <span className="text-[#CFA86A] italic" data-id="element-157">Cambian</span> Tu Vida
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-xl md:text-2xl text-[#F7F5F2]/90 mb-12 leading-relaxed font-light max-w-2xl mx-auto drop-shadow-lg" data-id="element-158">
            Cómo acceder a respuestas tan obvias sobre amor, dinero y bienestar
            que ignorarlas sería sabotear tu propia vida.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.45,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-col items-center gap-6" data-id="element-159">
            <button onClick={scrollToBonuses} className="px-10 py-5 rounded-lg bg-[#CFA86A] text-[#0C0A09] text-lg font-bold tracking-wide transition-all duration-300 hover:brightness-110 flex items-center gap-2" style={{
            boxShadow: '0 0 30px rgba(207, 168, 106, 0.4)'
          }} data-id="element-160">
              Descarga la Guía Ahora
              <span data-id="element-161">→</span>
            </button>
            <p className="text-sm text-[#F7F5F2]/70 font-light tracking-wide drop-shadow-md" data-id="element-162">
              Lectura de 5 minutos • Resultados inmediatos
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}